import { useAuthStore } from '~/stores/authStore'
import { AUTHENTICATED_USER_QUERY } from '~/graphql/auth'

/**
 * Session heartbeat
 * -----------------
 * Periodically pings the server to verify the session is still valid.
 * Without this, the user wouldn't notice an expired session until
 * their next action — and the action would fail in whatever
 * half-broken state the page is in. With the heartbeat, the redirect
 * to login happens proactively, with the page they were on captured
 * as `redirect=` so they land back in place after re-auth.
 *
 * Defensive behaviour:
 *   - Skip when not authenticated, when a logout is in progress, or
 *     when the tab is hidden (saves requests + no UX value if the
 *     user isn't looking).
 *   - On network / server errors we keep silent — they're not
 *     evidence the session is bad, and kicking the user out on a
 *     flaky connection is worse than leaving the session in place.
 *   - We bypass `authStore.fetchAuthenticatedUser` and call the
 *     query directly so we can distinguish "server says no user"
 *     (kick) from "request crashed" (ignore). The store's wrapper
 *     conflates the two by always clearing auth in its catch block.
 */

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export default defineNuxtPlugin(nuxtApp => {
  const apolloClient = nuxtApp.$apollo?.defaultClient

  if (!apolloClient) return

  const authStore = useAuthStore()
  const router = useRouter()

  setInterval(async () => {
    if (!authStore.isAuthenticated) return
    if (authStore.isLoggingOut) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return

    try {
      const { data } = await apolloClient.query({
        query: AUTHENTICATED_USER_QUERY,
        fetchPolicy: 'network-only',
      })

      if (data?.authenticatedItem) {
        // Still good — keep the local user object in sync in case
        // server-side fields changed (role, name, etc.) since login.
        return
      }

      // Server confirms there's no authenticated user. Same UX as
      // the Apollo error link's session-expired path: clear local
      // state, send them to login with the path captured so we can
      // bounce them back after re-auth.
      authStore.clearAuth()

      const currentPath = window.location.pathname + window.location.search
      const safePath = currentPath.startsWith('/account/login') ? '/follow-ups' : currentPath

      await router.replace({
        path: '/account/login',
        query: { redirect: safePath, reason: 'session_expired' },
      })
    } catch (err) {
      // Network/server issue — silent. The next user action will
      // surface real problems through Apollo's error link.
      console.warn('[AuthHeartbeat] check failed', err)
    }
  }, HEARTBEAT_INTERVAL_MS)
})
